import { html, joinHtml } from "./html.js";
import { formatUnitValue, unitPreference } from "./units.js";

const CURRENT_SPEED_COLOR_STOPS = [
  [0, [22, 58, 128]], [0.22, [40, 92, 178]], [0.45, [98, 70, 186]],
  [0.68, [172, 60, 170]], [0.86, [232, 86, 118]], [1, [252, 158, 72]]
];

function interpolateColor(position, stops) {
  const clamped = Math.max(0, Math.min(1, Number(position) || 0));
  const upper = stops.findIndex(([stop]) => clamped <= stop);
  const [highStop, highColor] = stops[Math.max(0, upper)];
  const [lowStop, lowColor] = stops[Math.max(0, upper - 1)];
  const fraction = highStop === lowStop ? 0 : (clamped - lowStop) / (highStop - lowStop);
  return lowColor.map((channel, index) => Math.round(channel + (highColor[index] - channel) * fraction)).join(", ");
}

export function currentSpeedLabel(metersPerSecond) {
  if (!Number.isFinite(Number(metersPerSecond))) return "—";
  const unit = unitPreference("speed");
  const factors = { kph: 3.6, mph: 2.236936, kn: 1.943844 };
  const value = Number(metersPerSecond) * (factors[unit] || 1);
  const decimals = value === 0 ? 0 : value < 0.095 ? 2 : value < 9.95 ? 1 : 0;
  return `${value.toFixed(decimals)} ${unit === "kph" ? "km/h" : unit}`;
}

export function currentProfileDepthLabel(meters) {
  if (meters <= 0.25) return "Surface";
  return formatUnitValue(meters, "depth", "m", { decimals: 0 });
}

export function currentDirectionLabel(degrees) {
  const points = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];
  return points[Math.round(degrees / 22.5) % points.length];
}

export function currentColor(speed) {
  const rgb = interpolateColor((Number(speed) || 0) / 0.5, CURRENT_SPEED_COLOR_STOPS);
  return `rgb(${rgb})`;
}

export function currentProfileHtml(profile, selectedDepthMeters = null, { meta = "" } = {}) {
  const values = [...(profile.values || [])]
    .filter((value) => Number.isFinite(value.depthMeters) && value.depthMeters >= 0
      && Number.isFinite(value.speedMetersPerSecond) && value.speedMetersPerSecond >= 0)
    .sort((a, b) => a.depthMeters - b.depthMeters);
  if (!values.length) return html`<p class="great-lakes-current-empty">No current profile is available at this point. Try another point on the lake.</p>`;

  const hasSelectedDepth = selectedDepthMeters !== null && Number.isFinite(Number(selectedDepthMeters));
  const selectedDepth = hasSelectedDepth ? Number(selectedDepthMeters) : 0;
  const closest = hasSelectedDepth
    ? values.reduce((best, value, index) => Math.abs(value.depthMeters - selectedDepth) < Math.abs(values[best].depthMeters - selectedDepth) ? index : best, 0)
    : -1;
  const maximumSpeed = Math.max(...values.map((value) => value.speedMetersPerSecond), 0.01);
  const rows = joinHtml(values.map((value, index) => {
    const speed = value.speedMetersPerSecond;
    const still = speed < 0.001;
    const hasDirection = Number.isFinite(value.directionDegrees);
    const bearing = hasDirection ? ((Math.round(value.directionDegrees) % 360) + 360) % 360 : null;
    const direction = still ? "Still" : hasDirection
      ? `Toward ${currentDirectionLabel(value.directionDegrees)} · ${bearing}°` : "Direction unavailable";
    const width = speed ? Math.max(3, speed / maximumSpeed * 100) : 0;
    const arrow = still || !hasDirection ? "" : html`<svg viewBox="0 0 20 20" aria-hidden="true" style="transform:rotate(${bearing}deg)"><path d="M10 17V3m0 0L5 8m5-5 5 5" /></svg>`;
    const directionContent = still ? "Still" : hasDirection
      ? html`<span><span class="great-lakes-current-toward">Toward </span>${currentDirectionLabel(value.directionDegrees)} <small>${bearing}°</small></span>`
      : "Direction unavailable";
    return html`<div class="great-lakes-current-row${index === closest ? " is-closest" : ""}" role="listitem" aria-label="${currentProfileDepthLabel(value.depthMeters)}: ${currentSpeedLabel(speed)}, ${direction}">
      <span class="great-lakes-current-depth">${currentProfileDepthLabel(value.depthMeters)}</span>
      <span class="great-lakes-current-track" aria-hidden="true"><i style="width:${width}%;background:${currentColor(speed)}"></i></span>
      <strong>${currentSpeedLabel(speed)}</strong>
      <span class="great-lakes-current-direction">${arrow}${directionContent}</span>
    </div>`;
  }), "");
  return html`${meta ? html`<p class="great-lakes-current-meta">${meta}</p>` : ""}
    <div class="great-lakes-current-chart" role="list" aria-label="Current speed and direction by depth">${rows}</div>`;
}
